import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { LOGO_URL } from "@/components/Logo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Lock, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f6f8fb] p-6">
      <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-sm p-8 animate-fade-up">
        <div className="flex items-center gap-2 mb-6">
          <div className="h-10 w-10 rounded-lg bg-[#0a2540] flex items-center justify-center"><img src={LOGO_URL} className="h-7 w-7" alt="logo" /></div>
          <span className="font-display font-extrabold text-xl text-[#0a2540]">MyMentor</span>
        </div>

        {done ? (
          <div data-testid="reset-success">
            <div className="flex items-center gap-2 text-emerald-600 mb-2"><CheckCircle2 className="h-6 w-6" /><h2 className="font-display font-bold text-xl text-[#0a2540]">Password Updated</h2></div>
            <p className="text-slate-500 text-sm">Redirecting you to login...</p>
          </div>
        ) : (
          <>
            <h2 className="font-display font-bold text-2xl text-[#0a2540]">Reset Password</h2>
            <p className="text-slate-500 mt-1 text-sm">Enter your new password below.</p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              {error && <div data-testid="reset-error" className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
              {!token && <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-700">Missing reset token.</div>}
              <div className="space-y-1.5">
                <Label htmlFor="new-pw">New Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input id="new-pw" data-testid="new-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="pl-9 h-11 focus-visible:ring-[#1e5bff]" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirm-pw">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input id="confirm-pw" data-testid="confirm-password-input" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="pl-9 h-11 focus-visible:ring-[#1e5bff]" />
                </div>
              </div>
              <button data-testid="reset-submit-button" type="submit" disabled={loading || !token} className="w-full h-11 rounded-lg bg-[#1e5bff] text-white font-semibold hover:bg-[#154cdb] transition-colors duration-200 flex items-center justify-center disabled:opacity-60">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Update Password"}
              </button>
            </form>
          </>
        )}
        <Link to="/login" className="mt-6 inline-block text-sm text-slate-500 hover:text-[#1e5bff] transition-colors duration-200">Return to Login</Link>
      </div>
    </div>
  );
}
